import { PermissionT } from "../../../../shared/domain/entities";
import { QueryClient } from "@tanstack/react-query";
/*
 *  TODO: - Stop using when the project is migrated.
 * import { clearStorage } from "../../../../old/utils/storage";
 */
// eslint-disable-next-line import/no-namespace -- This is to avoid auth-provider methods collapsing with auth-context methods
import * as services from "../services";
import { AuthContextState, AuthT } from "../../domain/entities";
import {
  FullPageErrorFallback,
  FullPageSpinner,
  useAsync,
} from "../../../../shared/infrastructure";
import React, { createContext, useContext, useEffect } from "react";
import { getAuthFromCache, removeAuthInCache } from "../utils/auth-storage";

const bootstrapAppData = async (): Promise<AuthT | null> => {
  const auth = getAuthFromCache();

  if (auth) {
    try {
      return Promise.resolve(auth);
    } catch (error) {
      removeAuthInCache();

      return null;
    }
  }

  return null;
};

const AuthContext = createContext<AuthContextState | null>(null);

type AuthProviderProps = {
  children?: React.ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps): React.ReactElement => {
  const { run, setData, ...userQuery } = useAsync<AuthT | null>();

  useEffect(() => {
    run(bootstrapAppData());
  }, [run]);

  if (userQuery.status === "resolved") {
    return (
      <AuthContext.Provider value={{ user: userQuery.data, setData }}>
        {children}
      </AuthContext.Provider>
    );
  }

  if (userQuery.status === "rejected") {
    return <FullPageErrorFallback error={userQuery.error} />;
  }

  return <FullPageSpinner />;
};

const useAuth = (): AuthContextState => {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }

  return context;
};

const login = async (
  setData: (data: AuthT | null) => void,
  credentials: Parameters<typeof services.login>[0]
): Promise<AuthT> => {
  const auth = await services.login(credentials);

  setData(auth);

  return auth;
};

const logout = (
  setData: (data: AuthT | null) => void,
  queryClient: QueryClient
): void => {
  queryClient.clear();
  removeAuthInCache();

  /*
   * TODO: - Stop using when the project is migrated.
   * void clearStorage();
   */

  setData(null);
};

const useHasPermissions = (
  permissions:
    | PermissionT["permission_id"]
    | Array<PermissionT["permission_id"]>
): boolean => {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error(`useHasPermissions must be used within a AuthProvider`);
  }

  const { user } = context;
  const permissionList = Array.isArray(permissions)
    ? permissions
    : [permissions];

  return (
    user?.role.permissions.reduce<boolean>(
      (prev, permission) =>
        prev || permissionList.includes(permission.permission_id),
      false
    ) || false
  );
};

export { AuthProvider, useAuth, login, logout, useHasPermissions };
